import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { fetchTasksForDateRange, fetchRecurringTemplates, checkPositionSanity } from "../services/firebaseService";
import { getLocalDateString, getRangeForMode, createLocalDate } from "../utilities/dateUtils";
import { nativePost, isNativeWebView, onNativeMessage } from "../nativeBridge.ts";
import { useAuth } from "./AuthContext";
import { useUserProfile } from "./UserProfileContext";
import { startTasksInitialLoadTime, stopTasksInitialLoadTime } from "../utilities/perfTraces";

const TaskContext = createContext();

const EMPTY_GROUPED = { todo: [], inProgress: [], completed: [] };

// Expand virtual recurring tasks for a given date
function expandVirtualRecurringTasks(templates, dateStr, materializedTasks, startDate, endDate) {
  if (!templates || templates.length === 0) return [];

  const date = createLocalDate(dateStr);
  const weekday = date.getDay(); // 0 = Sunday, 6 = Saturday
  const today = getLocalDateString(new Date());

  const virtualTasks = [];

  for (const template of templates) {
    // Check if template matches this weekday
    if (!template.weekdays || !Array.isArray(template.weekdays) || !template.weekdays.includes(weekday)) {
      continue;
    }

    // Don't show virtual recurring tasks on past days; materialized/parent tasks still show via materializedTasks
    if (dateStr < today) continue;

    // Check if there's already a task for this date using deterministic occurrence ID
    // Format: recurring:{templateId}:{YYYY-MM-DD}
    const occurrenceId = `recurring:${template.id}:${dateStr}`;
    
    // Check for existing task using deterministic occurrence ID
    const hasExistingOccurrence = materializedTasks.some(
      (task) => {
        // Check for materialized instance using deterministic occurrence ID
        if (task.recurringOccurrenceId === occurrenceId) {
          return true;
        }
        // Check for materialized instance using recurringTemplateId + date
        if (task.recurringTemplateId === template.id && task.isMaterializedInstance && task.date === dateStr) {
          return true;
        }
        return false;
      }
    );

    // Check for parent task (original source task)
    const hasParentTask = materializedTasks.some(
      (task) => {
        // Check for original source task (parent) - must match template ID, date, and not be virtual/materialized
        if (task.recurringTemplateId === template.id && !task.isMaterializedInstance && !task.isVirtual) {
          return task.date === dateStr;
        }
        // Check if this is the parent task using stored parent info
        if (template.parentTaskId && template.parentTaskDate) {
          if (task.id === template.parentTaskId && task.date === template.parentTaskDate) {
            return true; // Parent task exists for this date
          }
        }
        return false;
      }
    );

    // Also check if this date is the parent task's date (prevent creating virtual task on parent's date)
    if (template.parentTaskDate === dateStr) {
      // This is the parent task's date - don't create a virtual task here
      // The parent task itself should be shown, not a virtual duplicate
      continue;
    }

    // Skip if any existing occurrence or parent task found
    if (hasExistingOccurrence || hasParentTask) {
      continue; // Skip, task already exists for this date
    }

    // Additional check: Prevent virtual tasks from appearing immediately after parent deletion
    // When a parent task is deleted, the template is removed from Firestore, but there's a
    // brief moment where it still exists in recurringTemplates state before the snapshot
    // listener removes it.
    //
    // Solution: Check if the parent task exists by looking for it in the fetched tasks.
    // The template stores parentTaskId and parentTaskDate, so we can check if that specific
    // task exists. If the parent is outside the fetched range, we can't check, but that's OK
    // because if the parent was deleted, the template would be deleted too (and removed from state).
    if (template.parentTaskId && template.parentTaskDate) {
      // Check if the parent task exists in the fetched range
      const parentExists = materializedTasks.some(
        (task) => task.id === template.parentTaskId && task.date === template.parentTaskDate
      );

      const today = getLocalDateString(new Date());
      const isTodayOrPast = dateStr <= today;
      // Only skip when the parent's date is inside the fetch range: if it's in range
      // and not found, it was likely deleted. If it's outside range, we can't know,
      // so do not skip (avoids incorrectly hiding virtuals on today when parent is
      // from a different date outside the fetched window).
      const parentInRange = template.parentTaskDate >= startDate && template.parentTaskDate <= endDate;
      if (parentInRange && isTodayOrPast && !parentExists) {
        continue;
      }
    } else {
      // Legacy templates (no parentTaskId/parentTaskDate): we cannot know if the
      // parent would be in the fetched range, so do not skip.
    }

    // Create virtual task
    const virtualTask = {
      id: `virtual:${template.id}:${dateStr}`, // Stable deterministic ID
      title: template.title || "Untitled Task",
      section: template.section || "todo",
      priority: template.priority || "Medium",
      status: template.status || "todo",
      description: template.description ?? "",
      links: template.links || [],
      subtasks: template.subtasks || [],
      categories: template.categories || [],
      date: dateStr,
      workspace: template.workspace,
      uid: template.uid,
      position: 0,
      // Virtual task markers
      isVirtual: true,
      recurringTemplateId: template.id,
      instanceDate: dateStr,
      weekdays: template.weekdays || [], // Include weekdays for recurring modal
    };

    virtualTasks.push(virtualTask);
  }

  return virtualTasks;
}

function groupTasksForDate(fetchedTasks, dateStr, virtualTasks = []) {
  // Combine real tasks and virtual tasks
  const allTasks = [
    ...(fetchedTasks || []).filter((t) => t?.date === dateStr),
    ...virtualTasks,
  ];

  const grouped = allTasks.reduce(
    (acc, task) => {
      const section = task.section || "todo";
      if (!acc[section]) acc[section] = [];
      acc[section].push(task);
      return acc;
    },
    { todo: [], inProgress: [], completed: [] }
  );

  // Sort each section by priority then position, then reindex to ensure stable ordering
  const priorityOrder = { High: 1, Medium: 2, Low: 3 };
  const sortAndReindex = (tasks) => {
    if (!Array.isArray(tasks) || tasks.length === 0) return [];
    
    // Sort by priority first, then by position
    const sorted = [...tasks].sort((a, b) => {
      const aPriority = priorityOrder[a.priority] || 99;
      const bPriority = priorityOrder[b.priority] || 99;
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      // Within same priority, sort by position
      const aPos = a.position ?? 999;
      const bPos = b.position ?? 999;
      return aPos - bPos;
    });
    
    // Reindex to compact positions (0, 1, 2, 3...)
    return sorted.map((task, index) => ({
      ...task,
      position: index,
    }));
  };

  return {
    todo: sortAndReindex(grouped.todo),
    inProgress: sortAndReindex(grouped.inProgress),
    completed: sortAndReindex(grouped.completed),
  };
}

function countsFromGrouped(grouped) {
  const total =
    (grouped.todo?.length ?? 0) +
    (grouped.inProgress?.length ?? 0) +
    (grouped.completed?.length ?? 0);
  const done = grouped.completed?.length ?? 0;
  return { done, total };
}

function findTaskInCollections(taskId, grouped, weeklyTasks) {
  if (!taskId) return null;

  const groupedAll = [
    ...(grouped?.todo ?? []),
    ...(grouped?.inProgress ?? []),
    ...(grouped?.completed ?? []),
  ];
  const inGrouped = groupedAll.find((t) => t?.id === taskId);
  if (inGrouped) return inGrouped;

  const inWeekly = (weeklyTasks ?? []).find((t) => t?.id === taskId);
  if (inWeekly) return inWeekly;

  return null;
}

export const TaskProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  const { workspaces, workspacesLoading } = useUserProfile();

  const [tasks, setTasks] = useState(EMPTY_GROUPED);
  const [weeklyTasks, setWeeklyTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [fetchMode, setFetchMode] = useState("day"); // "day" | "week" | "month"
  const [isLoading, setIsLoading] = useState(true);
  /** Range and mode for the *last* completed fetch. Analytics uses this to avoid showing stale data. */
  const [fetchRange, setFetchRange] = useState(null); // { startDate, endDate, fetchMode }

  const [selectedTask, setSelectedTask] = useState(null);

  // Recurring templates cache (fetched once per workspace)
  const [recurringTemplates, setRecurringTemplates] = useState([]);

  const [selectedWorkspace, setSelectedWorkspace] = useState(() => {
    const saved =
      typeof window !== "undefined" ? localStorage.getItem("selectedWorkspace") : null;
    return saved || "work";
  });

  // workspaces now comes from UserProfileContext (single onSnapshot on users/{uid})

  // Prevent spamming native with identical widget updates
  const lastWidgetSentRef = useRef({ done: null, total: null, dateISO: null });

  // Cache latest Today counts to respond to WIDGET_SYNC_REQUEST immediately
  const todayCountsRef = useRef({ dateISO: null, done: 0, total: 0 });

  // If native asks for sync before we have counts, force-send on next snapshot
  const forceWidgetSendRef = useRef(false);

  // Keep latest collections accessible without stale closures
  const latestCollectionsRef = useRef({
    grouped: EMPTY_GROUPED,
    weekly: [],
  });

  // Track pending optimistic updates to prevent Firestore listener from causing jitter
  // This is set by MainPage when doing drag operations
  const pendingOptimisticUpdateRef = useRef(null);
  
  // Function to set pending optimistic update (called from MainPage during drag)
  const setPendingOptimisticUpdate = useCallback((update) => {
    pendingOptimisticUpdateRef.current = update;
    // Auto-clear after 500ms
    if (update) {
      setTimeout(() => {
        if (pendingOptimisticUpdateRef.current === update) {
          pendingOptimisticUpdateRef.current = null;
        }
      }, 500);
    }
  }, []);

  /**
   * Pending push open request.
   * Good UX rules:
   * - Only auto-open for a short window
   * - Cancel immediately if user interacts
   * - Never loop forever
   */
  const pendingOpenRef = useRef(null);
  // { taskId, taskDate?, taskWorkspace?, expiresAt, attempts, canceled }

  const cancelPendingPushOpen = () => {
    if (!pendingOpenRef.current) return;
    pendingOpenRef.current = null;
  };

  // Cancel auto-open if user starts interacting (prevents “late surprise open”)
  useEffect(() => {
    if (!isNativeWebView()) return;

    const cancel = () => {
      if (!pendingOpenRef.current) return;
      pendingOpenRef.current = null;
    };

    // Capture early so we cancel even if something else stops propagation
    const opts = { capture: true, passive: true };

    window.addEventListener("pointerdown", cancel, opts);
    window.addEventListener("touchstart", cancel, opts);
    window.addEventListener("keydown", cancel, { capture: true });
    window.addEventListener("wheel", cancel, opts);

    return () => {
      window.removeEventListener("pointerdown", cancel, opts);
      window.removeEventListener("touchstart", cancel, opts);
      window.removeEventListener("keydown", cancel, { capture: true });
      window.removeEventListener("wheel", cancel, opts);
    };
  }, []);

  const maybeSendWidgetSummary = (dateISO, done, total, force = false) => {
    if (!isNativeWebView()) return;

    const last = lastWidgetSentRef.current;
    const unchanged = last.dateISO === dateISO && last.done === done && last.total === total;

    if (unchanged && !force) return;

    lastWidgetSentRef.current = { dateISO, done, total };

    // Get To Do tasks for the widget, ordered by priority (High, Medium, Low) then position
    const todoTasks = latestCollectionsRef.current.grouped.todo || [];
    const priorityOrder = { High: 1, Medium: 2, Low: 3 };
    const sortedTodoTasks = [...todoTasks]
      .sort((a, b) => {
        const aPriority = priorityOrder[a.priority] || 99;
        const bPriority = priorityOrder[b.priority] || 99;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return (a.position ?? 999) - (b.position ?? 999);
      })
      .map(task => ({
        title: task.title || "Untitled Task",
        priority: task.priority || "Medium",
      }))
      .slice(0, 10); // Limit to 10 tasks for widget storage

    nativePost({
      type: "WIDGET_SET_TODAY_SUMMARY",
      payload: { 
        dateISO, 
        done, 
        total, 
        updatedAt: Date.now(),
        todoTasks: sortedTodoTasks,
      },
    });
  };

  // Persist workspace (id) and sync to Tidle Desktop native helper prefs file
  useEffect(() => {
    try {
      localStorage.setItem("selectedWorkspace", selectedWorkspace);
    } catch {
      // ignore
    }
    if (typeof window !== "undefined" && window.electron?.setPrefs) {
      window.electron.setPrefs({ selectedWorkspace }).catch(() => {});
    }
  }, [selectedWorkspace]);

  // Tidle Desktop: on load, apply workspace from native helper prefs file so menu bar stays in sync
  useEffect(() => {
    if (typeof window === "undefined" || !window.electron?.getPrefs) return;
    window.electron
      .getPrefs()
      .then((prefs) => {
        if (prefs?.selectedWorkspace) {
          setSelectedWorkspace(prefs.selectedWorkspace);
          try {
            localStorage.setItem("selectedWorkspace", prefs.selectedWorkspace);
          } catch (_) {}
        }
      })
      .catch(() => {});
  }, []);

  // Normalize selectedWorkspace when workspaces load: resolve legacy name→id or set to first workspace id
  useEffect(() => {
    if (!workspaces?.length) return;
    const ids = workspaces.map((w) => w.id);
    const names = workspaces.map((w) => w.name);
    const current = selectedWorkspace;
    let outcome = 'unchanged';
    if (ids.includes(current)) {
      fetch('http://127.0.0.1:7242/ingest/498cd0fb-fdab-4ec0-838d-96bff2445ad2',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'86c793'},body:JSON.stringify({sessionId:'86c793',location:'TaskContext.js:normalize',message:'workspace unchanged (valid id)',data:{current,ids,names,outcome},timestamp:Date.now(),hypothesisId:'H2,H3'})}).catch(()=>{});
      return;
    } else {
      const byName = workspaces.find((w) => w.name === current);
      if (byName) {
        outcome = 'resolvedByName';
        setSelectedWorkspace(byName.id);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/498cd0fb-fdab-4ec0-838d-96bff2445ad2',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'86c793'},body:JSON.stringify({sessionId:'86c793',location:'TaskContext.js:normalize',message:'workspace normalized',data:{current,ids,names,outcome,newValue:byName.id},timestamp:Date.now(),hypothesisId:'H2,H3'})}).catch(()=>{});
        // #endregion
        return;
      }
      if (names.includes(current)) {
        const ws = workspaces.find((w) => w.name === current);
        if (ws) {
          setSelectedWorkspace(ws.id);
          outcome = 'resolvedByNameMatch';
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/498cd0fb-fdab-4ec0-838d-96bff2445ad2',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'86c793'},body:JSON.stringify({sessionId:'86c793',location:'TaskContext.js:normalize',message:'workspace normalized',data:{current,ids,names,outcome,newValue:ws.id},timestamp:Date.now(),hypothesisId:'H2,H3'})}).catch(()=>{});
          // #endregion
        }
        return;
      }
      outcome = 'defaultToFirst';
      setSelectedWorkspace(workspaces[0].id);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/498cd0fb-fdab-4ec0-838d-96bff2445ad2',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'86c793'},body:JSON.stringify({sessionId:'86c793',location:'TaskContext.js:normalize',message:'workspace normalized',data:{current,ids,names,outcome,newValue:workspaces[0].id},timestamp:Date.now(),hypothesisId:'H2,H3'})}).catch(()=>{});
      // #endregion
    }
  }, [workspaces]);

  // Reset state when user logs out
  useEffect(() => {
    if (!uid) {
      setTasks(EMPTY_GROUPED);
      setWeeklyTasks([]);
      setSelectedTask(null);
      setIsLoading(false);

      lastWidgetSentRef.current = { done: null, total: null, dateISO: null };
      todayCountsRef.current = { dateISO: null, done: 0, total: 0 };
      forceWidgetSendRef.current = false;

      latestCollectionsRef.current = { grouped: EMPTY_GROUPED, weekly: [] };
      pendingOpenRef.current = null;
      setFetchRange(null);
    }
  }, [uid]);

  const today = getLocalDateString();
  const uiIsExactlyTodayDayView = fetchMode === "day" && selectedDate === today;

  const openTaskById = (taskId) => {
    const { grouped, weekly } = latestCollectionsRef.current;
    const found = findTaskInCollections(taskId, grouped, weekly);
    if (found) {
      setSelectedTask(found);
      return true;
    }
    return false;
  };

  const maybeApplyPendingContext = (pending) => {
    if (!pending) return;

    // We only do minimal “helpful” navigation:
    // switch to day mode and date if provided.
    // Workspace switch is only done if provided (so no surprise changes).
    if (pending.taskWorkspace && pending.taskWorkspace !== selectedWorkspace) {
      setSelectedWorkspace(pending.taskWorkspace);
    }

    if (pending.taskDate && pending.taskDate !== selectedDate) {
      if (fetchMode !== "day") setFetchMode("day");
      setSelectedDate(pending.taskDate);
    }
  };

  const attemptOpenPending = () => {
    const pending = pendingOpenRef.current;
    if (!pending?.taskId) return false;

    if (pending.canceled) {
      pendingOpenRef.current = null;
      return false;
    }

    // Timebox: if it didn’t open quickly, do not surprise-open later
    if (pending.expiresAt && Date.now() > pending.expiresAt) {
      pendingOpenRef.current = null;
      return false;
    }

    const attempts = (pending.attempts ?? 0) + 1;
    pendingOpenRef.current = { ...pending, attempts };

    const opened = openTaskById(pending.taskId);
    if (opened) {
      pendingOpenRef.current = null;
      return true;
    }

    // Hard cap attempts so it cannot “keep trying” for long
    if (attempts >= 4) {
      pendingOpenRef.current = null;
    }

    return false;
  };

  // 0) Fetch recurring templates once per workspace (cached)
  useEffect(() => {
    if (!uid || !selectedWorkspace) {
      setRecurringTemplates([]);
      return;
    }

    const unsubscribe = fetchRecurringTemplates(uid, selectedWorkspace, (templates) => {
      setRecurringTemplates(templates || []);
    });

    return () => unsubscribe?.();
  }, [uid, selectedWorkspace]);

  // 1) Main UI subscription (day/week/month)
  // Only subscribe once selectedWorkspace is a valid workspace id (avoids querying with legacy "work" before normalization).
  useEffect(() => {
    if (!uid || !selectedWorkspace || !selectedDate) return;
    if (workspacesLoading || !workspaces?.length || !workspaces.some((w) => w.id === selectedWorkspace)) return;

    const { startDate, endDate } = getRangeForMode(selectedDate, fetchMode);
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/498cd0fb-fdab-4ec0-838d-96bff2445ad2',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'86c793'},body:JSON.stringify({sessionId:'86c793',location:'TaskContext.js:fetchEffect',message:'fetch subscription using workspace',data:{selectedWorkspace,selectedDate,startDate,endDate,fetchMode},timestamp:Date.now(),hypothesisId:'H1,H2'})}).catch(()=>{});
    // #endregion

    // Debounce loading state: only show loading if data doesn't arrive quickly
    // This prevents UI flash when data loads from cache or is very fast
    const loadingTimeout = setTimeout(() => {
      setIsLoading(true);
    }, 100);

    // Start tasks_initial_load_time trace before attaching listener
    startTasksInitialLoadTime(uid, selectedWorkspace, startDate, endDate);

    let isFirstSnapshot = true;
    const unsubscribe = fetchTasksForDateRange(
      uid,
      startDate,
      endDate,
      selectedWorkspace,
      (fetchedTasks) => {
        // Stop tasks_initial_load_time trace on first snapshot only
        if (isFirstSnapshot) {
          stopTasksInitialLoadTime(uid, selectedWorkspace, startDate, endDate);
          isFirstSnapshot = false;
        }

        // Clear the loading timeout since data arrived
        clearTimeout(loadingTimeout);
        setIsLoading(false);
        setFetchRange({ startDate, endDate, fetchMode });

        // Expand virtual recurring tasks for the selected date
        const virtualTasks = expandVirtualRecurringTasks(
          recurringTemplates,
          selectedDate,
          fetchedTasks,
          startDate,
          endDate
        );

        // Group tasks (real + virtual) for the selected date
        const groupedForSelected = groupTasksForDate(
          fetchedTasks,
          selectedDate,
          virtualTasks
        );

        // Check if there's a pending optimistic update that we should preserve
        // This prevents Firestore listener from overwriting optimistic drag updates
        setTasks((prevTasks) => {
          const pending = pendingOptimisticUpdateRef.current;
          
          // If there's a pending optimistic update and it's recent (within 500ms)
          if (pending && Date.now() - pending.timestamp < 500 && pending.taskId) {
            // Check if the task is already in the expected section in current state
            const taskInExpectedSection = 
              prevTasks[pending.toSection]?.some(t => t.id === pending.taskId);
            const taskInFirestoreSection = 
              groupedForSelected[pending.toSection]?.some(t => t.id === pending.taskId);
            
            // If task is in expected section in current state but Firestore hasn't caught up yet,
            // keep the optimistic state to prevent jitter
            if (taskInExpectedSection && !taskInFirestoreSection) {
              return prevTasks; // Keep optimistic update
            }
          }
          
          // Normal update from Firestore
          // Sanity check positions after grouping
          checkPositionSanity(groupedForSelected.todo, "todo");
          checkPositionSanity(groupedForSelected.inProgress, "inProgress");
          checkPositionSanity(groupedForSelected.completed, "completed");
          
          return groupedForSelected;
        });
        setWeeklyTasks(fetchedTasks);

        // Include virtual tasks in latestCollectionsRef for task lookup
        const allTasksForSelected = [
          ...fetchedTasks.filter((t) => t?.date === selectedDate),
          ...virtualTasks,
        ];

        latestCollectionsRef.current = {
          grouped: groupedForSelected,
          weekly: fetchedTasks || [],
        };

        // If we have a pending push open, try to resolve it immediately
        attemptOpenPending();

        // Native widget sync optimization:
        // If the main subscription range already includes Today, compute Today counts from this snapshot
        // and avoid needing an extra overlapping Today listener.
        if (isNativeWebView()) {
          const rangeIncludesToday = startDate <= today && today <= endDate;
          if (rangeIncludesToday) {
            const virtualTasksForToday = expandVirtualRecurringTasks(
              recurringTemplates,
              today,
              fetchedTasks,
              startDate,
              endDate
            );
            const groupedForToday = groupTasksForDate(
              fetchedTasks,
              today,
              virtualTasksForToday
            );
            const { done, total } = countsFromGrouped(groupedForToday);

            todayCountsRef.current = { dateISO: today, done, total };

            const force = forceWidgetSendRef.current;
            forceWidgetSendRef.current = false;

            maybeSendWidgetSummary(today, done, total, force);
          }
        }
      }
    );

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe?.();
    };
  }, [uid, selectedWorkspace, selectedDate, fetchMode, uiIsExactlyTodayDayView, today, recurringTemplates, workspacesLoading, workspaces]);

  // 2) Dedicated Today subscription (only when needed)
  useEffect(() => {
    if (!uid || !selectedWorkspace) return;
    if (!isNativeWebView()) return;
    if (uiIsExactlyTodayDayView) return;

    // If the main subscription range already includes Today, it will keep the widget in sync.
    // Avoid a duplicate overlapping listener in that case.
    const mainRange = getRangeForMode(selectedDate, fetchMode);
    const mainIncludesToday = mainRange.startDate <= today && today <= mainRange.endDate;
    if (mainIncludesToday) return;

    const { startDate, endDate } = getRangeForMode(today, "day");

    const unsubscribe = fetchTasksForDateRange(
      uid,
      startDate,
      endDate,
      selectedWorkspace,
      (fetchedTasks) => {
        // Expand virtual recurring tasks for today
        const virtualTasks = expandVirtualRecurringTasks(
          recurringTemplates,
          today,
          fetchedTasks,
          startDate,
          endDate
        );

        const groupedForToday = groupTasksForDate(fetchedTasks, today, virtualTasks);
        const { done, total } = countsFromGrouped(groupedForToday);

        todayCountsRef.current = { dateISO: today, done, total };

        const force = forceWidgetSendRef.current;
        forceWidgetSendRef.current = false;

        maybeSendWidgetSummary(today, done, total, force);
      }
    );

    return () => unsubscribe?.();
  }, [
    uid,
    selectedWorkspace,
    uiIsExactlyTodayDayView,
    today,
    recurringTemplates,
    selectedDate,
    fetchMode,
  ]);

  // 3) Listen for native sync requests and force-send current Today summary
  useEffect(() => {
    if (!isNativeWebView()) return;

    const unsub = onNativeMessage((m) => {
      if (m?.type !== "WIDGET_SYNC_REQUEST") return;

      forceWidgetSendRef.current = true;

      const { dateISO, done, total } = todayCountsRef.current;

      if (dateISO) {
        maybeSendWidgetSummary(dateISO, done, total, true);
        forceWidgetSendRef.current = false;
      }
    });

    return () => unsub?.();
  }, []);

  // 4) Notification tap / cold start open
  useEffect(() => {
    if (!isNativeWebView()) return;

    const unsub = onNativeMessage((m) => {
      if (m?.type !== "PUSH_OPENED") return;

      const data = m?.payload?.data ?? {};
      const taskId = data?.taskId;
      if (!taskId) return;

      // Set a short window where auto-open is allowed
      const pending = {
        taskId,
        taskDate: typeof data?.taskDate === "string" ? data.taskDate : undefined,
        taskWorkspace: typeof data?.taskWorkspace === "string" ? data.taskWorkspace : undefined,
        expiresAt: Date.now() + 1500, // best practice: prevent late “steal”
        attempts: 0,
        canceled: false,
      };

      pendingOpenRef.current = pending;

      // First try right away (works if already loaded)
      if (attemptOpenPending()) return;

      // Then help the app load the right context if provided
      maybeApplyPendingContext(pending);

      // If it still hasn't opened quickly, it will self-expire and not surprise-open later
      setTimeout(() => {
        const cur = pendingOpenRef.current;
        if (!cur) return;
        if (cur.taskId !== taskId) return;
        if (cur.expiresAt && Date.now() > cur.expiresAt) {
          cancelPendingPushOpen();
        }
      }, 1700);
    });

    return () => unsub?.();
  }, []);

  // Function to optimistically remove a template from recurringTemplates state
  // This is used when deleting a template to prevent virtual tasks from being created
  // while waiting for the Firestore snapshot to update
  const removeRecurringTemplate = useCallback((templateId) => {
    setRecurringTemplates((prev) => prev.filter((t) => t.id !== templateId));
  }, []);

  return (
    <TaskContext.Provider
      value={{
        tasks,
        setTasks,
        selectedDate,
        setSelectedDate,
        selectedWorkspace,
        setSelectedWorkspace,
        weeklyTasks,
        workspaces,
        workspacesLoading,
        fetchMode,
        setFetchMode,
        fetchRange,
        isLoading,
        selectedTask,
        setSelectedTask,
        openTaskById,
        removeRecurringTemplate,
        setPendingOptimisticUpdate,

        // optional, useful for debugging
        cancelPendingPushOpen,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTasks = () => useContext(TaskContext);